import { type FocusBuilding } from '../../../../../../models/FocusBuilding'
import { useEffect, useState } from 'react'
import type { ThingsToConsiderSection } from '../../ThingsToConsiderCard'
import { BasicTable, type BasicTableColumn } from '../../BasicTable'
import { BlueButton } from '../../../../../customComponents/buttons/BlueButton'
import { AddCircle, HomeOutlined } from '@mui/icons-material'
import { ThingsToConsiderButton } from '../../ThingsToConsiderButton'
import { Box, CircularProgress, Typography } from '@mui/material'
import { AddOrEditFocusBuildingsModal } from './AddOrEditFocusBuildingsModal'
import { ClearFocusBuildingTracking, GetFocusBuildings, LockActivity, UnlockActivity } from '../../../../../../services/AnnualPlanService'
import { type AnnualPlanRole } from '../../../../../../models/AnnualPlanRole'
import { PriorityFourActivityName } from '../../../../../../models/PriorityFourActivityName'
import { DeleteFocusBuildingModal } from './DeleteFocusBuildingsModal'
import { TrackFocusBuildingModal } from './TrackFocusBuildingModal'
import { SubBasicTable } from '../../SubBasicTable'
import { ClearTrackingModal } from '../../../../implementation/ClearTrackingModal'
import { handleApiResponse } from '../../../../../../utils/apiHelpers'

interface Props {
  priorityId: number
  planOrgId: number
  isdOrgId: number
  userRole: AnnualPlanRole | null
  readOnly: boolean
  trackingReadonly: boolean
  showTableOnly?: boolean
  hideExpand?: boolean
  implementationPhase?: boolean
  budgetOverviewView?: boolean
}

type ModalState = 'add' | 'update' | 'track' | 'delete' | 'clearTrack' | 'none'
type FocusBuildingRow = (FocusBuilding & {
  organizationInfo: JSX.Element
  buildingInfo: JSX.Element
  budget: string
  expandedContent?: JSX.Element
})

export const FocusBuildingsList = (props: Props): JSX.Element => {
  const [currentItem, setCurrentItem] = useState<FocusBuilding | undefined>(undefined)
  const [modalState, setModalState] = useState<ModalState>('none')
  const [focusBuildings, setFocusBuildings] = useState<FocusBuilding[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [rows, setRows] = useState<FocusBuildingRow[]>([])
  const implementationPhase: boolean = props.implementationPhase ?? false

  useEffect(() => {
    void loadFocusBuildings()
  }, [])

  useEffect(() => {
    if (modalState === 'none') {
      setCurrentItem(undefined)
    }
  }, [modalState])

  useEffect(() => {
    setRows(focusBuildings.map(fb => {
      const budget = fb.adjustedBudget != null ? `$${fb.adjustedBudget}` : `$${fb.budgetRequest}`
      return {
        ...fb,
        isUpdated: fb.isUpdate,
        isTracked: fb.isUpdate && fb.progress != null,
        organizationInfo: <>
          <Typography variant='body1' fontWeight='700'>{fb.organization.name}</Typography>
          <Typography variant='body1'>{fb.organizationContactName}</Typography>
          <Typography variant='body1'>{fb.organizationContactEmail}</Typography>
        </>,
        buildingInfo: <>
          <Typography variant='body1' fontWeight='700'>{fb.building.name}</Typography>
          <Typography variant='body1'>{fb.buildingContactName}</Typography>
          <Typography variant='body1'>{fb.buildingContactEmail}</Typography>
        </>,
        budget,
        expandedContent: expandedContent(fb)
      }
    })
    )
  }, [focusBuildings])

  const expandedContent = (fb: FocusBuilding): JSX.Element | undefined => {
    if (fb.progress == null || (!fb.progress.inProgress && !fb.progress.notStarted && !fb.progress.programExpanded && !fb.progress.newProgramEstablished && fb.actualBudget == null)) {
      return undefined
    }

    let progressString: string = ''
    const progressParts: string[] = []
    if (fb.progress.notStarted) { progressParts.push('Not Started') }
    if (fb.progress.inProgress) { progressParts.push('In Progress') }
    if (fb.progress.programExpanded) { progressParts.push('Program Expanded') }
    if (fb.progress.newProgramEstablished) { progressParts.push('New Program Established') }
    if (progressParts.length > 0) {
      progressString = progressParts.join(', ')
    }
    const subTableRows: [{ id: number, progress: string, actualAmount?: string }] = [{ id: fb.id, progress: progressString, actualAmount: `$${fb.actualBudget ?? 0}` }]

    return <Box
      sx={{
        p: '2rem',
        backgroundColor: 'var(--grey-100)'
      }}
    >
      <SubBasicTable
        hasTrackingDetails
        readonly={props.readOnly}
        trackingReadonly={props.trackingReadonly}
        onEditTracking={async () => { await startModal('track', fb) }}
        onDeleteTracking={async () => { await startModal('clearTrack', fb) }}
        columns={[
          { id: 'progress', label: 'PROGRESS', minWidth: 200, isTrackingColumn: true },
          { id: 'actualAmount', label: 'ACTUAL DOLLAR AMOUNT', minWidth: 200, isTrackingColumn: true },
          { id: 'trackingAction', label: '', isTrackingColumn: true }
        ]}
        rows={subTableRows}
        dataTestId={`${fb.id}-expandedFocusBuildingTable`}
      />
    </Box>
  }

  const startModal = async (state: ModalState, focusBuilding?: FocusBuilding): Promise<void> => {
    if (focusBuilding != null) {
      await LockActivity(focusBuilding.id, PriorityFourActivityName.FocusBuildings, focusBuilding.isUpdate)
      setCurrentItem(focusBuilding)
    }
    setModalState(state)
  }

  const handleCloseModal = async (): Promise<void> => {
    if (currentItem != null) {
      await UnlockActivity(currentItem.id, PriorityFourActivityName.FocusBuildings, currentItem.isUpdate)
    }
    setModalState('none')
  }

  const loadFocusBuildings = async (): Promise<void> => {
    try {
      setIsLoading(true)
      setModalState('none')

      const buildings = await handleApiResponse(GetFocusBuildings(props.priorityId, !implementationPhase))
      if (buildings != null) setFocusBuildings(buildings)
    } catch (error: unknown) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const thingsToConsiderSections: ThingsToConsiderSection[] = [
    {
      title: 'Examples of Activities to Develop and Expand Programs to Increase Opportunities for Students',
      items: [
        'Work with building teams and administrators to establish new Peer to Peer programs',
        'Review Peer to Peer program data to identify buildings for further development',
        'Work with program leaders to expand existing programs to support additional students with autism',
        'Recruit a broad range of peers to participate in Peer to Peer programs',
        'Develop formalized programs at the elementary building level',
        'Develop accredited programs (grades 6-12)',
        'Collect Focus Student data from students with autism who are participating in Peer to Peer',
        'Offer county-level Peer to Peer training',
        'Provide training and information about Peer to Peer to administrators',
        'Complete Fidelity Checklists for new and existing Peer to Peer programs',
        'Establish and fund a Peer to Peer coordinator for the ISD'
      ]
    }
  ]

  const columns: BasicTableColumn[] = [
    { id: 'organizationInfo', label: 'ISD/District Info', minWidth: 200 },
    { id: 'buildingInfo', label: 'Building(s) Info', minWidth: 200 },
    { id: 'planType', label: 'Plan Type', minWidth: 100 },
    { id: 'actions', label: 'Action(s)', minWidth: 100 },
    { id: 'resources', label: 'Resources', minWidth: 100 },
    { id: 'budget', label: 'Budget', minWidth: 100 }
  ]

  return <>
    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton onClick={async () => { await startModal('add') }} dataTestId='addFocusBuildingButton'>
            Add Focus Building <AddCircle sx={{ ml: '10px' }} fontSize='small' />
          </BlueButton>
      }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='focusBuildingsThingsToConsiderButton'
            thingsToConsiderSections={thingsToConsiderSections}
            cardDataTestId='focusBuildingsThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Box
      data-testid='focusBuildingsList'
      sx={{
        border: '1px solid var(--light-blue-300)',
        backgroundColor: 'var(--white)',
        borderRadius: '8px',
        padding: focusBuildings.length === 0 ? '24px' : '0',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 10px 10px -8px #3B353E4D'
      }}
    >
      {isLoading
        ? <CircularProgress sx={{ alignSelf: 'center' }} />
        : (focusBuildings.length === 0
            ? <>
              <HomeOutlined sx={{ color: 'var(--light-blue-300)', width: '144px', height: '144px', alignSelf: 'center' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                <Typography variant='h4'>No Focus Buildings Added</Typography>
                {!props.readOnly &&
                  <Typography variant='body1' fontSize='16px'>
                    Use the Add Focus Building button to add a Focus Building to your Annual Plan.
                  </Typography>
                }
              </Box>
            </>
            : <BasicTable
                dataTestId='focusBuildingsTable'
                indexRowIds={true}
                columns={columns}
                rows={rows}
                isLoading={isLoading}
                readonly={props.readOnly}
                trackingReadonly={props.trackingReadonly}
                onEdit={async (row: FocusBuildingRow) => { await startModal('update', row) }}
                onDelete={async (row: FocusBuildingRow) => { await startModal('delete', row) }}
                onUpdate={async (row: FocusBuildingRow) => { await startModal('update', row) }}
                onTrack={async (row: FocusBuildingRow) => { await startModal('track', row) }}
                hideExpand={props.hideExpand}
                implementationPhase={implementationPhase}
                budgetOverviewView={props.budgetOverviewView}
            />
          )
      }

      {modalState === 'track' && currentItem != null &&
        <TrackFocusBuildingModal
          initialValues={currentItem}
          onClose={handleCloseModal}
          onSaved={loadFocusBuildings}
        />
      }

      {(modalState === 'add' || (modalState === 'update' && currentItem != null)) &&
        <AddOrEditFocusBuildingsModal
          open={true}
          onClose={handleCloseModal}
          onSaved={loadFocusBuildings}
          priorityId={props.priorityId ?? 0}
          planOrgId={props.planOrgId}
          isdOrgId={props.isdOrgId}
          initialValues={currentItem}
          implementationPhase={implementationPhase}
        />
      }

      {modalState === 'delete' && currentItem != null &&
        <DeleteFocusBuildingModal
          focusBuilding={currentItem}
          onConfirm={loadFocusBuildings}
          onClose={handleCloseModal}
        />
      }

      {modalState === 'clearTrack' && currentItem != null &&
        <ClearTrackingModal
          onConfirm={async () => {
            await ClearFocusBuildingTracking({ id: currentItem.id })
            void loadFocusBuildings()
          }}
          onClose={handleCloseModal}
        />
      }
    </Box>
  </>
}
